import React from 'react';
import { TextField, type TextFieldProps } from '@mui/material';

/**
 * The typed text of a whole-number field, as a number no lower than `min` — or
 * null while it is not a number yet (an emptied field).
 */
export function parseWholeNumber(text: string, min = 0): number | null {
  if (text.trim() === '') return null;
  const n = parseInt(text, 10);
  return Number.isNaN(n) ? null : Math.max(min, n);
}

type WholeNumberFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number;
  onChange: (value: number) => void;
  min?: number;
};

/**
 * A number input that keeps the TEXT being typed, not just the number.
 *
 * Bound straight to a number (`parseInt(v) || 0`), emptying the field put "0"
 * back in the box at once, and typing 5 after clearing it could give 50 — the
 * recurring "Generate N days before the period ends" field did exactly that
 * (caught by crm_invoice_cadence_and_preview.cy.ts, 2026-10-02). Here an
 * emptied field stays empty, `onChange` fires only on a valid entry, a number
 * below `min` shows as `min` at once, and leaving the field shows the number
 * the form holds.
 */
export function WholeNumberField({ value, onChange, min = 0, onBlur, inputProps, ...rest }: WholeNumberFieldProps) {
  const [draft, setDraft] = React.useState(String(value));
  React.useEffect(() => {
    setDraft((d) => (parseWholeNumber(d, min) === value ? d : String(value)));
  }, [value, min]);
  return (
    <TextField
      {...rest}
      type="number"
      inputProps={{ min, ...inputProps }}
      value={draft}
      onChange={(e) => {
        const n = parseWholeNumber(e.target.value, min);
        // A number below `min` shows as `min` at once, so the box never
        // displays a value the form is not going to use.
        setDraft(n !== null && n !== parseInt(e.target.value, 10) ? String(n) : e.target.value);
        if (n !== null) onChange(n);
      }}
      onBlur={(e) => {
        setDraft(String(value));
        onBlur?.(e);
      }}
    />
  );
}
